import React, { useRef, useState, useMemo, useEffect } from 'react';
import firebaseDB from '../../../../firebase';
import { COMPANY_PATHS, WORKER_PATHS } from '../../../../utils/dataPaths';
import { useAuth } from "../../../../context/AuthContext";

// Import components from Invoice folder
import CustomInvoiceForm from './Invocie/CustomInvoiceForm';
import ThankYouMessageForm from './Invocie/ThankYouMessageForm';
import InvoiceHistoryTable from './Invocie/InvoiceHistoryTable';
import DeletedInvoicesTable from './Invocie/DeletedInvoicesTable';
import PaymentModal from './Invocie/PaymentModal';
import { DeleteConfirmModal, RestoreConfirmModal } from './Invocie/ConfirmModals';

// Import utility functions
import {
  resolvePhoto,
  normalizeWorker,
  calculateDaysCount,
  calculateInvoiceAmount,
  calculateTotalAmount,
  formatDate,
  formatDateTime,
  formatINR,
  formatAmount,
  formatWorkerName,
  thankYouMessages,
  determineServiceType,
  getCurrentThankYouMessage,
  getPaymentDetails
} from './Invocie/invoiceUtils';

const CompanyInvoice = ({
  company,
  worker = null,
  payments = [],
  billTitle = 'Company Invoice',
  billNumber = '',
  getTranslation
}) => {
  const iframeRef = useRef(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showCustomInvoiceForm, setShowCustomInvoiceForm] = useState(false);
  const [showThankYouMessageForm, setShowThankYouMessageForm] = useState(false);
  const [selectedThankYouType, setSelectedThankYouType] = useState('default');
  const [customThankYouMessage, setCustomThankYouMessage] = useState('');
  const [invoiceHistory, setInvoiceHistory] = useState([]);
  const [deletedInvoices, setDeletedInvoices] = useState([]);
  const [activeTab, setActiveTab] = useState('preview');
  const [invoiceCounter, setInvoiceCounter] = useState(0);
  const [saveMessage, setSaveMessage] = useState({ type: '', text: '' });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);
  const [deleteRemarks, setDeleteRemarks] = useState('');
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
  const [invoiceToRestore, setInvoiceToRestore] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [selectedWorkerId, setSelectedWorkerId] = useState('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentData, setPaymentData] = useState({
    amount: '',
    date: new Date().toISOString().split('T')[0],
    remarks: ''
  });

  // Invoice modes
  const INVOICE_MODE = {
    CREATE: "create",
    EDIT: "edit",
    VIEW: "view"
  };

  const [invoiceMode, setInvoiceMode] = useState(INVOICE_MODE.CREATE);
  const [isEditingExisting, setIsEditingExisting] = useState(false);
  const [editingInvoiceId, setEditingInvoiceId] = useState(null);

  const [invoiceData, setInvoiceData] = useState({
    serviceDate: '',
    endDate: '',
    invoiceDate: new Date().toISOString().split('T')[0],
    dayAmount: '',
    gapIfAny: '',
    travelingCharges: '',
    extraCharges: '',
    remarks: '',
    additionalComments: '',
    serviceRemarks: '',
    nextPaymentDate: '',
    thankYouType: 'default',
    customThankYou: '',
    workerId: '',
    workerName: '',
    workerDepartment: '',
    workerPhone: '',
    workerPhoto: '',
    invoiceNumber: '',
    workerSnapshot: null
  });

  const headerImage = "https://firebasestorage.googleapis.com/v0/b/jenceo-admin.firebasestorage.app/o/OfficeFiles%2FHeadder.svg?alt=media&token=fa65a3ab-ba03-4959-bc36-e293c6db48ae";
  const defaultCompanyPhoto = "https://firebasestorage.googleapis.com/v0/b/jenceo-admin.firebasestorage.app/o/OfficeFiles%2FSample-Photo.jpg?alt=media&token=01855b47-c9c2-490e-b400-05851192dde7";

  // Get current user from AuthContext
  const { user: authUser } = useAuth();

  // Get current user name
  const getCurrentUser = () => {
    return authUser?.name || authUser?.username || "System";
  };

  // Enhanced photo resolver
  const enhancedResolvePhoto = (photo) => {
    return resolvePhoto(photo, defaultCompanyPhoto);
  };

  // Global worker search function
  const searchWorkerGlobally = async (workerId) => {
    if (!workerId || workerId.trim() === "") return null;

    try {
      const departments = Object.keys(WORKER_PATHS);

      for (const department of departments) {
        const path = WORKER_PATHS[department];
        const workersRef = firebaseDB.child(path);

        let snapshot = await workersRef
          .orderByChild("workerId")
          .equalTo(workerId.trim())
          .once('value');

        let data = snapshot.val();

        if (!data) {
          snapshot = await workersRef
            .orderByChild("idNo")
            .equalTo(workerId.trim())
            .once('value');

          data = snapshot.val();
        }

        if (data) {
          const workerKey = Object.keys(data)[0];
          const workerData = { ...data[workerKey], department: department };

          if (workerData.idNo && !workerData.workerId) {
            workerData.workerId = workerData.idNo;
          }

          if ((workerData.firstName || workerData.lastName) && !workerData.workerName) {
            workerData.workerName = `${workerData.firstName || ""} ${workerData.lastName || ""}`.trim();
          }

          if (workerData.mobileNo1 && !workerData.workerCell1) {
            workerData.workerCell1 = workerData.mobileNo1;
          }

          if (workerData.mobileNo2 && !workerData.workerCell2) {
            workerData.workerCell2 = workerData.mobileNo2;
          }

          // Get employee photo URL
          if (workerKey) {
            const photoPath = `${path}/${workerKey}/employeePhoto`;
            const photoSnapshot = await firebaseDB.child(photoPath).once('value');
            const photoData = photoSnapshot.val();
            if (photoData) {
              workerData.photo = enhancedResolvePhoto(photoData);
            }
          }

          // Fallback: company worker photo
          if (!workerData.photo && company?.companyId) {
            const companyInfo = await findCompanyKey(company.companyId);
            if (companyInfo) {
              const companyWorkerPath =
                `${companyInfo.path}/${companyInfo.key}/WorkerData/${workerKey}`;

              const companyWorkerSnap = await firebaseDB.child(companyWorkerPath).once('value');
              const companyWorker = companyWorkerSnap.val();

              if (companyWorker) {
                workerData.photo = enhancedResolvePhoto(
                  companyWorker.employeePhoto ||
                  companyWorker.employeePhotoUrl ||
                  ''
                );
              }
            }
          }

          return workerData;
        }
      }

      return null;
    } catch (error) {
      console.error("Error searching worker globally:", error);
      return null;
    }
  };

  const findCompanyKey = async (companyId) => {
    if (!companyId) return null;

    const categories = Object.keys(COMPANY_PATHS);

    for (const category of categories) {
      const basePath = COMPANY_PATHS[category];

      try {
        const companiesRef = firebaseDB.child(basePath);
        const snapshot = await companiesRef
          .orderByChild("companyId")
          .equalTo(companyId)
          .once('value');

        const data = snapshot.val();

        if (data) {
          const key = Object.keys(data)[0];
          return {
            key: key,
            category: category,
            path: basePath
          };
        }
      } catch (error) {
        continue;
      }
    }

    return null;
  };

  // Helper function to generate invoice number
  const generateNewInvoiceNumber = () => {
    if (billNumber) return billNumber;

    // If editing an existing invoice, use its invoice number
    if (invoiceMode === INVOICE_MODE.EDIT && editingInvoiceId) {
      const existingInvoice = invoiceHistory.find(inv => inv.id === editingInvoiceId);
      if (existingInvoice) return existingInvoice.invoiceNumber;
    }

    // If invoiceData already has an invoice number (e.g., during creation), use it
    if (invoiceData.invoiceNumber && invoiceData.invoiceNumber.trim() !== '') {
      return invoiceData.invoiceNumber;
    }

    const now = new Date();
    const year = now.getFullYear().toString().slice(-2); // Last 2 digits of year
    const month = now.toLocaleString('en-US', { month: 'short' }); // Jan, Feb, etc
    const companyId = company?.companyId || 'CO-HC-001'; // Use company ID or default

    // Count ALL invoices for this company (not just current month) to get sequential number
    const companyInvoices = invoiceHistory.filter(inv =>
      inv.companyId === company?.companyId &&
      !inv.isDeleted
    );

    // Get the latest invoice number for this company to find the next sequential number
    let nextInvoiceNumber = 1;

    if (companyInvoices.length > 0) {
      // Find the highest number in existing invoice numbers
      const invoiceNumbers = companyInvoices
        .map(inv => {
          const match = inv.invoiceNumber.match(/CO-HC-\d+-\w+-\d+-(\d+)$/);
          return match ? parseInt(match[1]) : 0;
        })
        .filter(num => num > 0);

      if (invoiceNumbers.length > 0) {
        nextInvoiceNumber = Math.max(...invoiceNumbers) + 1;
      } else {
        // If no numbered invoices found, count all invoices
        nextInvoiceNumber = companyInvoices.length + 1;
      }
    }

    // Generate the invoice number: CO-HC-001-Jan-26-6
    return `CO-HC-${companyId.replace(/^CO-HC-/, '').padStart(3, '0')}-${month}-${year}-${nextInvoiceNumber}`;
  };

  // Generate invoice ID (for Firebase storage) - use the same invoice number as key
  const generateInvoiceId = () => {
    const invoiceNumber = generateNewInvoiceNumber();
    // Convert to valid Firebase key (replace dots and special chars with underscores)
    return invoiceNumber.replace(/[.\-\s]/g, '_');
  };

  // Save invoice to Firebase
  const saveInvoiceToFirebase = async (invoiceObj) => {
    if (!company?.companyId) {
      return null;
    }

    try {
      const companyInfo = await findCompanyKey(company.companyId);
      if (!companyInfo) {
        return null;
      }

      // Ensure worker snapshot and invoice number are properly saved
      const invoiceToSave = {
        ...invoiceObj,
        key: undefined,
        data: {
          ...invoiceObj.data,
          serviceDate: invoiceObj.data.serviceDate || '',
          endDate: invoiceObj.data.endDate || '',
          invoiceDate: invoiceObj.data.invoiceDate || '',
          dayAmount: invoiceObj.data.dayAmount || '',
          gapIfAny: invoiceObj.data.gapIfAny || '',
          travelingCharges: invoiceObj.data.travelingCharges || '',
          extraCharges: invoiceObj.data.extraCharges || '',
          remarks: invoiceObj.data.remarks || '',
          additionalComments: invoiceObj.data.additionalComments || '',
          serviceRemarks: invoiceObj.data.serviceRemarks || '',
          nextPaymentDate: invoiceObj.data.nextPaymentDate || '',
          thankYouType: invoiceObj.data.thankYouType || 'default',
          customThankYou: invoiceObj.data.customThankYou || '',
          workerId: invoiceObj.data.workerId || '',
          workerName: invoiceObj.data.workerName || '',
          workerDepartment: invoiceObj.data.workerDepartment || '',
          workerPhone: invoiceObj.data.workerPhone || '',
          workerPhoto: enhancedResolvePhoto(invoiceObj.data.workerPhoto || ''),
          invoiceNumber: invoiceObj.invoiceNumber || generateNewInvoiceNumber(),
          workerSnapshot: invoiceObj.data.workerSnapshot || {
            workerId: invoiceObj.data.workerId,
            workerName: invoiceObj.data.workerName,
            department: invoiceObj.data.workerDepartment,
            phone: invoiceObj.data.workerPhone,
            photo: enhancedResolvePhoto(invoiceObj.data.workerPhoto || '')
          }
        }
      };

      delete invoiceToSave.key;

      const invoiceRef = firebaseDB.child(
        `${companyInfo.path}/${companyInfo.key}/Invoice`
      );

      // Use invoice number as the key (converted to Firebase-friendly format)
      const invoiceKey = generateInvoiceId();

      if (isEditingExisting && editingInvoiceId) {
        // For editing, use the existing key
        await invoiceRef.child(editingInvoiceId).update(invoiceToSave);
      } else {
        // For new invoice, use the generated invoice number as key
        await invoiceRef.child(invoiceKey).set(invoiceToSave);
      }

      return invoiceKey;

    } catch (error) {
      console.error("Error saving invoice to Firebase:", error);
      throw error;
    }
  };

  // Load invoices from Firebase
  const loadInvoicesFromFirebase = async () => {
    if (!company?.companyId) {
      return;
    }

    try {
      const companyInfo = await findCompanyKey(company.companyId);
      if (!companyInfo) {
        return;
      }

      const invoiceRef = firebaseDB.child(
        `${companyInfo.path}/${companyInfo.key}/Invoice`
      );

      const snapshot = await invoiceRef.once('value');
      const invoicesData = snapshot.val();

      if (invoicesData) {
        const invoicesArray = Object.entries(invoicesData).map(([key, value]) => ({
          id: key,
          ...value
        }));

        const activeInvoices = invoicesArray.filter(inv => !inv.isDeleted);
        const deletedInvoices = invoicesArray.filter(inv => inv.isDeleted);

        // Sort active invoices by createdAt date (newest first)
        const sortedActiveInvoices = activeInvoices.sort((a, b) =>
          new Date(b.createdAt || b.date || 0) - new Date(a.createdAt || a.date || 0)
        );

        // Sort deleted invoices by deletedAt date (newest first)
        const sortedDeletedInvoices = deletedInvoices.sort((a, b) =>
          new Date(b.deletedAt || 0) - new Date(a.deletedAt || 0)
        );

        setInvoiceHistory(sortedActiveInvoices);
        setDeletedInvoices(sortedDeletedInvoices);
      } else {
        setInvoiceHistory([]);
        setDeletedInvoices([]);
      }
    } catch (error) {
      console.error("Error loading invoices from Firebase:", error);
    }
  };

  const loadWorkers = async () => {
    if (!company?.companyId) {
      return;
    }

    try {
      const companyInfo = await findCompanyKey(company.companyId);

      if (!companyInfo) {
        setWorkers([]);
        return;
      }

      const workersPath = `${companyInfo.path}/${companyInfo.key}/WorkerData`;

      const workersRef = firebaseDB.child(workersPath);
      const snapshot = await workersRef.once('value');

      const workersData = snapshot.val();

      if (workersData) {
        const workersArray = await Promise.all(
          Object.entries(workersData).map(async ([key, value]) => {
            // Get employee photo
            let photo = '';
            try {
              const workerSnap = await firebaseDB.child(`${workersPath}/${key}`).once('value');
              const workerVal = workerSnap.val() || {};

              photo = enhancedResolvePhoto(
                workerVal.employeePhoto ||
                workerVal.employeePhotoUrl ||
                workerVal.profilePhoto ||
                workerVal.photo ||
                ''
              );

            } catch (error) {
            }

            return {
              key,
              ...value,
              photo
            };
          })
        );

        setWorkers(workersArray);

        if (worker) {
          const workerIdToMatch = worker.idNo || worker.workerId;
          const foundWorker = workersArray.find(w =>
            w.idNo === workerIdToMatch ||
            w.workerId === workerIdToMatch
          );

          if (foundWorker) {
            const normalized = normalizeWorker(foundWorker);
            const workerId = normalized.workerId;
            setSelectedWorkerId(workerId);

            setInvoiceData(prev => ({
              ...prev,
              workerId: workerId,
              workerName: normalized.workerName,
              workerDepartment: normalized.department,
              workerPhone: normalized.phone,
              workerPhoto: normalized.photo,
              workerSnapshot: normalized
            }));
          }
        }
      } else {
        setWorkers([]);
      }
    } catch (error) {
      console.error("Error loading workers:", error);
      setWorkers([]);
    }
  };

  // Auto-fill trigger
  useEffect(() => {
    if (!invoiceData.workerId) return;
    if (workers.length === 0) return;

    const found = workers.find(w =>
      w.workerId === invoiceData.workerId ||
      w.idNo === invoiceData.workerId
    );

    if (!found) return;

    const normalized = normalizeWorker(found);

    setInvoiceData(prev => ({
      ...prev,
      workerName: normalized.workerName,
      workerDepartment: normalized.department,
      workerPhone: normalized.phone,
      workerPhoto: normalized.photo,
      workerSnapshot: normalized
    }));

    setTimeout(() => {
      if (iframeRef.current) {
        iframeRef.current.srcdoc = buildInvoiceHTML();
      }
    }, 100);
  }, [invoiceData.workerId, workers]);

  // Determine service type for thank you message
  const determineCompanyServiceType = () => {
    return determineServiceType(company?.companyType);
  };

  // Get current thank you message
  const getCurrentThankYou = () => {
    return getCurrentThankYouMessage(
      invoiceData.thankYouType,
      invoiceData.customThankYou,
      company?.companyName
    );
  };

  useEffect(() => {
    if (company?.companyId) {
      loadWorkers();
      loadInvoicesFromFirebase();

      setInvoiceData(prev => ({
        ...prev,
        dayAmount: company?.serviceCharges || '',
        thankYouType: determineCompanyServiceType(),
        invoiceNumber: ''
      }));
    }
  }, [company]);

  const handleOpenInvoice = () => {
    setInvoiceMode(INVOICE_MODE.CREATE);
    setIsEditingExisting(false);
    setEditingInvoiceId(null);
    setShowInvoiceModal(true);
  };

  const findExistingInvoiceByServiceDate = (serviceDate) => {
    if (!serviceDate) return null;
    const existing = invoiceHistory.find(invoice =>
      invoice.data.serviceDate === serviceDate &&
      invoice.companyId === company?.companyId
    );
    return existing || null;
  };

  // FIXED: Correct save logic for create vs edit
  const saveInvoiceToHistory = async () => {
    const totalAmount = calculateTotalAmount(invoiceData);
    const currentUser = getCurrentUser();

    if (invoiceMode === INVOICE_MODE.EDIT && editingInvoiceId) {
      // EDIT MODE: Update existing invoice
      const existingInvoice = invoiceHistory.find(inv => inv.id === editingInvoiceId);
      if (!existingInvoice) {
        setSaveMessage({
          type: 'error',
          text: 'Invoice not found for editing'
        });
        setTimeout(() => setSaveMessage({ type: '', text: '' }), 3000);
        return;
      }

      const updatedInvoice = {
        ...existingInvoice,
        amount: totalAmount,
        data: {
          ...invoiceData,
          workerSnapshot: {
            workerId: invoiceData.workerId,
            workerName: invoiceData.workerName,
            department: invoiceData.workerDepartment,
            phone: invoiceData.workerPhone,
            photo: enhancedResolvePhoto(invoiceData.workerPhoto)
          }
        },
        paymentDetails: { ...paymentDetails },
        updatedAt: new Date().toISOString(),
        updatedBy: currentUser
      };

      try {
        await saveInvoiceToFirebase(updatedInvoice);
      } catch (error) {
        setSaveMessage({
          type: 'error',
          text: 'Error updating invoice in Firebase'
        });
        setTimeout(() => setSaveMessage({ type: '', text: '' }), 3000);
        return;
      }

      setInvoiceHistory(prev => prev.map(invoice =>
        invoice.id === editingInvoiceId ? updatedInvoice : invoice
      ));

      setSaveMessage({
        type: 'success',
        text: 'Invoice updated successfully!'
      });

      // Reset edit mode
      setInvoiceMode(INVOICE_MODE.CREATE);
      setIsEditingExisting(false);
      setEditingInvoiceId(null);

      setTimeout(() => setSaveMessage({ type: '', text: '' }), 3000);

      setTimeout(() => {
        if (iframeRef.current) {
          iframeRef.current.srcdoc = buildInvoiceHTML();
        }
      }, 100);

      return;
    } else {
      // CREATE MODE: Create new invoice
      // Generate new invoice number using the readable format
      const newInvoiceNumber = generateNewInvoiceNumber();

      // Generate invoice ID for Firebase storage (same as invoice number)
      const invoiceId = generateInvoiceId();

      const newInvoice = {
        id: invoiceId, // Use invoice number as ID
        invoiceNumber: newInvoiceNumber, // This is the readable number: CO-HC-001-Jan-26-6
        date: new Date().toISOString().split('T')[0],
        amount: totalAmount,
        companyName: company?.companyName || '',
        companyId: company?.companyId || '',
        workerName: invoiceData.workerName || formatWorkerName(worker),
        workerId: invoiceData.workerId || worker?.idNo || worker?.workerId || '',
        data: {
          ...invoiceData,
          workerSnapshot: {
            workerId: invoiceData.workerId,
            workerName: invoiceData.workerName,
            department: invoiceData.workerDepartment,
            phone: invoiceData.workerPhone,
            photo: enhancedResolvePhoto(invoiceData.workerPhoto)
          }
        },
        paymentDetails: { ...paymentDetails },
        createdAt: new Date().toISOString(),
        createdBy: currentUser,
        updatedAt: new Date().toISOString(),
        updatedBy: currentUser,
        isDeleted: false,
        deletedAt: null,
        deletedBy: null,
        deletedReason: null
      };

      try {
        await saveInvoiceToFirebase(newInvoice);
      } catch (error) {
        setSaveMessage({
          type: 'error',
          text: 'Error saving invoice to Firebase'
        });
        setTimeout(() => setSaveMessage({ type: '', text: '' }), 3000);
        return;
      }

      setInvoiceHistory(prev => [newInvoice, ...prev]);

      setSaveMessage({
        type: 'success',
        text: `Invoice ${newInvoiceNumber} saved successfully!`
      });

      setTimeout(() => setSaveMessage({ type: '', text: '' }), 5000);

      // Clear form after save
      setInvoiceData({
        serviceDate: '',
        endDate: '',
        invoiceDate: new Date().toISOString().split('T')[0],
        dayAmount: company?.serviceCharges || '',
        gapIfAny: '',
        travelingCharges: '',
        extraCharges: '',
        remarks: '',
        additionalComments: '',
        serviceRemarks: '',
        nextPaymentDate: '',
        thankYouType: determineCompanyServiceType(),
        customThankYou: '',
        workerId: '',
        workerName: '',
        workerDepartment: '',
        workerPhone: '',
        workerPhoto: '',
        invoiceNumber: '',
        workerSnapshot: null
      });
    }
  };

  const handleApplyCustomInvoice = (formData) => {
    setInvoiceData({
      ...formData,
      thankYouType: invoiceData.thankYouType,
      customThankYou: invoiceData.customThankYou,
      workerId: formData.workerId || invoiceData.workerId,
      workerName: formData.workerName || invoiceData.workerName,
      workerDepartment: formData.workerDepartment || invoiceData.workerDepartment,
      workerPhone: formData.workerPhone || invoiceData.workerPhone,
      workerPhoto: enhancedResolvePhoto(formData.workerPhoto || invoiceData.workerPhoto),
      invoiceNumber: invoiceMode === INVOICE_MODE.EDIT ? invoiceData.invoiceNumber : '',
      workerSnapshot: {
        workerId: formData.workerId || invoiceData.workerId,
        workerName: formData.workerName || invoiceData.workerName,
        department: formData.workerDepartment || invoiceData.workerDepartment,
        phone: formData.workerPhone || invoiceData.workerPhone,
        photo: enhancedResolvePhoto(formData.workerPhoto || invoiceData.workerPhoto)
      }
    });

    setShowCustomInvoiceForm(false);

    setTimeout(() => {
      if (iframeRef.current) {
        iframeRef.current.srcdoc = buildInvoiceHTML();
      }
    }, 100);
  };

  const handleThankYouTypeChange = (type, customMessage = '') => {
    setSelectedThankYouType(type);
    setInvoiceData(prev => ({
      ...prev,
      thankYouType: type,
      customThankYou: type === 'custom' ? customMessage : ''
    }));

    if (type !== 'custom') {
      setCustomThankYouMessage('');
    }
  };

  const paymentDetails = useMemo(() => getPaymentDetails(payments, company), [payments, company]);

  const generatedInvoiceNumber = useMemo(() => {
    if (billNumber) return billNumber;

    if (invoiceMode === INVOICE_MODE.EDIT && editingInvoiceId) {
      const existingInvoice = invoiceHistory.find(inv => inv.id === editingInvoiceId);
      if (existingInvoice) return existingInvoice.invoiceNumber;
    }

    if (invoiceData.invoiceNumber && invoiceData.invoiceNumber.trim() !== '') {
      return invoiceData.invoiceNumber;
    }

    // Otherwise generate new one with the new format
    return generateNewInvoiceNumber();
  }, [billNumber, company, invoiceHistory, editingInvoiceId, invoiceData.invoiceNumber, invoiceMode]);

  useEffect(() => {
    if (invoiceHistory.length > 0 && !isEditingExisting) {
      const currentMonthYear = new Date().toLocaleString('en-US', { month: 'short' }) + '-' + new Date().getFullYear().toString().slice(-2);
      const monthInvoices = invoiceHistory.filter(inv =>
        inv.invoiceNumber.includes(currentMonthYear) && !inv.isDeleted
      );
      setInvoiceCounter(monthInvoices.length);
    }
  }, [invoiceHistory, generatedInvoiceNumber, isEditingExisting]);

  const buildCompanyDetailsTable = () => {
    const safe = (v, d = "—") => (v == null || v === "" ? d : String(v));
    const formatINR = (value) => {
      const n = Number(value || 0);
      if (!isFinite(n)) return "₹0";
      try {
        return "₹" + n.toLocaleString("en-IN", { maximumFractionDigits: 0 });
      } catch {
        return "₹" + String(n);
      }
    };

    const companyPhone = safe(
      company?.officialPhone ||
      company?.primaryMobile ||
      company?.primaryContactPhone
    );

    const primaryContact = safe(
      company?.primaryContactName ||
      company?.primaryContact
    );

    const primaryMobile = safe(
      company?.primaryMobile ||
      company?.primaryContactPhone
    );

    const financeContact = safe(
      company?.financeContactName ||
      company?.financeContact
    );

    const financeMobile = safe(
      company?.financeMobile ||
      company?.financeContactPhone
    );

    const companyLocation = safe(
      company?.registeredDistrict ||
      company?.registeredVillage ||
      company?.registeredState
    );

    // Get worker photo - use workerSnapshot for saved invoices, otherwise current invoiceData
    const workerSnapshot = invoiceData.workerSnapshot || {};
    const workerPhoto = enhancedResolvePhoto(workerSnapshot.photo || invoiceData.workerPhoto || '');
    const workerName = workerSnapshot.workerName || invoiceData.workerName || '';
    const workerId = workerSnapshot.workerId || invoiceData.workerId || '';
    const workerDepartment = workerSnapshot.department || invoiceData.workerDepartment || '';
    const workerPhone = workerSnapshot.phone || invoiceData.workerPhone || '';

    return `
      <div class="company-section" style="margin: 20px 0;">
        <h4 style="background: linear-gradient(135deg, #02acf2 0%, #0266f2 100%); color: white; padding: 12px 15px; margin: 0; font-size: 16px; border-radius: 5px 5px 0 0;">Company Details</h4>
        <div style="background: #f8f9fa; border: 1px solid #dee2e6; border-top: none; border-radius: 0 0 5px 5px; padding: 15px;">
          
          <div class="section-header" style="background: #e9ecef; padding: 8px 12px; margin: -15px -15px 12px -15px; font-weight: bold; color: #0266f2; border-bottom: 1px solid #dee2e6;">
            Basic Information
          </div>
          
          <div class="grid-layout" style="display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 12px; margin-bottom: 20px;">
            <div class="info-item" style="background: white; padding: 10px; border-radius: 6px; border: 1px solid #e5e5e5;">
              <div style="font-size: 11px; color: #666; margin-bottom: 4px;">Company ID</div>
              <div style="font-size: 12px; font-weight: bold; color: #333;">${safe(company?.companyId)}</div>
            </div>
            <div class="info-item" style="background: white; padding: 10px; border-radius: 6px; border: 1px solid #e5e5e5;">
              <div style="font-size: 11px; color: #666; margin-bottom: 4px;">Company Name</div>
              <div style="font-size: 12px; font-weight: bold; color: #333;">${safe(company?.companyName)}</div>
            </div>
            <div class="info-item" style="background: white; padding: 10px; border-radius: 6px; border: 1px solid #e5e5e5;">
              <div style="font-size: 11px; color: #666; margin-bottom: 4px;">Company Type</div>
              <div style="font-size: 12px; font-weight: bold; color: #333;">${safe(company?.companyType)}</div>
            </div>

            <div class="info-item" style="background: white; padding: 10px; border-radius: 6px; border: 1px solid #e5e5e5;">
              <div style="font-size: 11px; color: #666; margin-bottom: 4px;">Official Phone</div>
              <div style="font-size: 12px; font-weight: bold; color: #333;">${companyPhone}</div>
            </div>
            <div class="info-item" style="background: white; padding: 10px; border-radius: 6px; border: 1px solid #e5e5e5;">
              <div style="font-size: 11px; color: #666; margin-bottom: 4px;">Official Email</div>
              <div style="font-size: 12px; font-weight: bold; color: #333;">${safe(company?.officialEmail)}</div>
            </div>
            <div class="info-item" style="background: white; padding: 10px; border-radius: 6px; border: 1px solid #e5e5e5;">
              <div style="font-size: 11px; color: #666; margin-bottom: 4px;">Website</div>
              <div style="font-size: 12px; font-weight: bold; color: #333;">${safe(company?.websiteUrl)}</div>
            </div>
          </div>
          
          <div class="section-header" style="background: #e9ecef; padding: 8px 12px; margin: 0 -15px 12px -15px; font-weight: bold; color: #0266f2; border-bottom: 1px solid #dee2e6;">
            Address & Contact Details
          </div>
          
          <div class="grid-layout" style="display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 12px;">
            <div class="info-item" style="background: white; padding: 10px; border-radius: 6px; border: 1px solid #e5e5e5;">
              <div style="font-size: 11px; color: #666; margin-bottom: 4px;">Registered Address</div>
              <div style="font-size: 12px; font-weight: bold; color: #333;">
                ${safe(company?.registeredBuilding)} ${safe(company?.registeredStreet)}, 
                ${safe(company?.registeredVillage)}, ${companyLocation}
              </div>
            </div>
            <div class="info-item" style="background: white; padding: 10px; border-radius: 6px; border: 1px solid #e5e5e5; background: linear-gradient(135deg, #f0f8ff 0%, #e6f7ea 100%);">
              <div style="font-size: 11px; color: #666; margin-bottom: 4px;">Primary Contact</div>
              <div style="font-size: 12px; font-weight: bold; color: #0266f2; font-size: 14px;">${primaryContact}</div>
              <div style="font-size: 11px; color: #666;">${primaryMobile}</div>
            </div>
            <div class="info-item" style="background: white; padding: 10px; border-radius: 6px; border: 1px solid #e5e5e5;">
              <div style="font-size: 11px; color: #666; margin-bottom: 4px;">Finance Contact</div>
              <div style="font-size: 12px; font-weight: bold; color: #333;">${financeContact}</div>
              <div style="font-size: 11px; color: #666;">${financeMobile}</div>
            </div>
          </div>
          
          ${workerName ? `
          <div class="section-header" style="background: #e9ecef; padding: 8px 12px; margin: 20px -15px 12px -15px; font-weight: bold; color: #2e7d32; border-bottom: 1px solid #dee2e6;">
            Assigned Worker Details
          </div>
          <div class="grid-layout" style="display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 12px;">
            ${workerPhoto ? `
            <div class="info-item" style="background: white; padding: 10px; border-radius: 6px; border: 1px solid #e5e5e5; text-align: center;">
              <div style="font-size: 11px; color: #666; margin-bottom: 8px;">Employee Photo</div>
              <img src="${workerPhoto}" alt="Employee" style="width: 100px; height: 100px; object-fit: cover; border-radius: 8px; border: 1px solid #7a7a7aff;" />
            
            </div>
            ` : `
            <div class="info-item" style="background: white; padding: 10px; border-radius: 6px; border: 1px solid #e5e5e5; text-align: center;">
              <div style="font-size: 11px; color: #666; margin-bottom: 8px;">Employee Photo</div>
              <img src="${defaultCompanyPhoto}" alt="Employee" style="width: 100px; height: 100px; object-fit: cover; border-radius: 8px; border: 1px solid #7a7a7aff;" />
              <div style="margin-top: 8px; font-size: 10px; color: #ff6b6b; font-weight: bold;">
                Using Default Photo
              </div>
            </div>
            `}
            
            <div class="info-item" style="background: white; padding: 10px; border-radius: 6px; border: 1px solid #e5e5e5;">
              <div style="font-size: 11px; color: #666; margin-bottom: 4px;">Worker ID</div>
              <div style="font-size: 12px; font-weight: bold; color: #333;">${safe(workerId)}</div>
              <br>
               <div style="font-size: 11px; color: #666; margin-bottom: 4px;">Worker Name</div>
              <div style="font-size: 12px; font-weight: bold; color: #0266f2; font-size: 14px;">${safe(workerName)}</div>
            </div>
            
            <div class="info-item" style="background: white; padding: 10px; border-radius: 6px; border: 1px solid #e5e5e5;">
              <div style="font-size: 11px; color: #666;">
                Department:
              </div>
              <div style="font-size: 12px; font-weight: bold; color: #333;">${safe(workerDepartment)}</div>
               
            </div>
          </div>
          ` : ''}
          
        </div>
        
        <style>
          @media (max-width: 768px) {
            .grid-layout {
              grid-template-columns: 1fr !important;
            }
            .info-item {
              margin-bottom: 8px;
            }
            .company-section {
              margin: 15px 0 !important;
            }
          }
          @media (max-width: 480px) {
            .info-item {
              padding: 8px !important;
            }
          }
        </style>
      </div>
    `;
  };

  const buildInvoiceHTML = () => {
    const currentDate = formatDate(new Date());
    const companyName = company?.companyName || 'Company';
    const companyId = company?.companyId || 'N/A';

    const companyLocation = company?.registeredDistrict ||
      company?.registeredVillage ||
      company?.registeredState ||
      'N/A';

    const companyPhone = company?.officialPhone ||
      company?.primaryMobile ||
      company?.primaryContactPhone ||
      'N/A';

    const serviceType = company?.companyType || 'N/A';
    const primaryContact = company?.primaryContactName ||
      company?.primaryContact ||
      'N/A';

    const contractStartDate = company?.contractStartDate ? formatDate(company.contractStartDate) : 'N/A';

    const gapInfo = invoiceData.gapIfAny || 'None';
    const travelingCharges = parseFloat(invoiceData.travelingCharges) || 0;
    const extraCharges = parseFloat(invoiceData.extraCharges) || 0;

    // Calculate days count and invoice amount
    const rawServiceDate = invoiceData.serviceDate;
    const rawEndDate = invoiceData.endDate;
    const daysCount = calculateDaysCount(rawServiceDate, rawEndDate);
    const dayAmount = parseFloat(invoiceData.dayAmount) || 0;
    const invoiceAmount = calculateInvoiceAmount(daysCount, dayAmount);

    // Use formatted dates for display
    const displayServiceDate = formatDate(rawServiceDate);
    const displayEndDate = formatDate(rawEndDate);

    const invoiceDate = invoiceData.invoiceDate ? formatDate(invoiceData.invoiceDate) : currentDate;

    const totalAmount = calculateTotalAmount(invoiceData);
    const totalPaid = paymentDetails.totalPaid || 0;
    const dueAmount = Math.max(0, totalAmount - totalPaid);

    const nextPaymentDate = invoiceData.nextPaymentDate ? formatDate(invoiceData.nextPaymentDate) : 'N/A';

    const thankYouMessage = getCurrentThankYou();

    const paymentSummaryHTML = paymentDetails.lastPayment ? `
      <div class="payment-card last-payment">
        <div class="payment-label">Last Payment</div>
        <div class="payment-amount last-payment">${formatINR(paymentDetails.lastPayment.amount)}</div>
        <small class="muted">${paymentDetails.lastPayment.date ? formatDate(paymentDetails.lastPayment.date) : 'N/A'} • ${paymentDetails.lastPayment.method}</small>
      </div>
    ` : `
      <div class="payment-card last-payment">
        <div class="payment-label">Last Payment</div>
        <div class="payment-amount last-payment">₹0.00</div>
        <small class="muted">No payments recorded</small>
      </div>
    `;

    const companyLogo = (company?.companyLogo && company.companyLogo.trim() !== '')
      ? company.companyLogo
      : (company?.companyLogoUrl && company.companyLogoUrl.trim() !== '')
        ? company.companyLogoUrl
        : defaultCompanyPhoto;

    const html = `
<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Invoice - ${companyName}</title>
  <style>
    *{box-sizing:border-box}
    html,body{margin:0;padding:0;font-family:Arial,Helvetica,sans-serif;color:#111}
    .page{ max-width:900px; margin:auto;background:#fff;border:1px solid #e5e5e5;padding:15px}
    .header{display:flex;align-items:flex-start;justify-content:space-between;gap:12px;border-bottom:2px solid #b7b4b4; padding:15px; margin:0 -15px; background: linear-gradient(135deg, #c7d1f5 0%, #e3e8ff 100%); border-radius: 5px;}
    
    .h-left{flex:1; margin-top:15px}
    .title{font-size:32px;font-weight:700;letter-spacing:.4px;margin:0;background:linear-gradient(135deg, #02acf2 0%, #0266f2 100%);-webkit-background-clip:text;-webkit-text-fill-color:transparent}
    .subtitle{font-size:12px;color:#444;margin-top:2px; font-weight:900}
    .meta{font-size:11px;color:#555;margin-top:4px;}
    .meta div {margin: 8px 0}
    .sec{margin-top:14px;border:1px solid #ddd;border-radius:8px;overflow:hidden;box-shadow:0 2px 4px rgba(0,0,0,0.1)}
    .sec-title{background:linear-gradient(135deg, #02acf2 0%, #0266f2 100%); padding:10px;font-weight:700;color:white}
    .sec-title h3{margin:0;font-size:14px}
    .sec-body{padding:12px}
    
    .table-responsive {
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
      margin: 10px 0;
    }
    
    .custom-invoice-section {
      background: #e3f1fdff;
      border: 1px solid #dee2e6;
      border-radius: 8px;
      padding: 12px;
    }
    
    .custom-invoice-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
      gap: 12px;
      margin-top: 8px;
    }
    
    .custom-invoice-item {
      padding: 8px;
      background: white;
      border-radius: 6px;
      border: 1px solid #e5e5e5;
      word-break: break-word;
    }
    
    .custom-invoice-label {
      font-weight: 600;
      font-size: 11px;
      color: #555;
      margin-bottom: 4px;
    }
    
    .custom-invoice-value {
      font-weight: 500;
      font-size: 12px;
      color: #333;
    }
    
    .muted{color:#777; font-size: 11px;}
    .footer{margin :15px -15px -15px -15px;font-size:10px; color:#fff;display:flex;justify-content:space-between; background:linear-gradient(135deg, #02acf2 0%, #0266f2 100%); padding:8px 15px}
    .blue {color:#02acf2}
    
    @media print{
      .page{border:none;margin:0;width:100%}
      .table-responsive {overflow: visible;}
    }
    
    .header-img{width:100%;max-height:100px;object-fit:contain;margin-bottom:6px}
    .photo-box{display:block;align-items:center;text-align:center}
    .photo-box .rating{ font-size:12px}
    .photo-box img{width:120px;height:120px;object-fit:cover;border-radius:8px;border:1px solid #7d7d7eff; box-shadow: 0 4px 8px rgba(0,0,0,0.1)}
    
    .payment-summary {
      display: grid; 
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); 
      gap: 12px; 
      margin: 15px 0
    }
    
    .payment-card {
      background: white; 
      border-radius: 8px; 
      padding: 12px; 
      box-shadow: 0 2px 8px rgba(0,0,0,0.1); 
      border-left: 4px solid
    }
    
    .payment-card.last-payment {border-left-color: #51cf66; background: linear-gradient(135deg, #f0fff4 0%, #e6f7ea 100%)}
    .payment-card.due-amount {border-left-color: #ff6b6b; background: linear-gradient(135deg, #fff5f5 0%, #ffecec 100%)}
    .payment-card.next-payment {border-left-color: #339af0; background: linear-gradient(135deg, #f0f8ff 0%, #e6f2ff 100%)}
    .payment-card.service-charges {border-left-color: #ff922b; background: linear-gradient(135deg, #fff4e6 0%, #ffebd6 100%)}
    .payment-card.total-paid {border-left-color: #cc5de8; background: linear-gradient(135deg, #f8f0fc 0%, #f3d9fa 100%)}
    
    .payment-label {font-size: 11px; color: #666; margin-bottom: 4px}
    .payment-amount {font-size: 16px; font-weight: bold}
    .payment-amount.last-payment {color: #51cf66}
    .payment-amount.due-amount {color: #ff6b6b}
    .payment-amount.next-payment {color: #339af0}
    .payment-amount.service-charges {color: #ff922b}
    .payment-amount.total-paid {color: #cc5de8}
    
    .company-section {
      margin: 15px 0;
      border: 1px solid #dee2e6;
      border-radius: 8px;
      overflow: hidden;
    }
    
    .thank-you {
      text-align:center; 
      padding:15px; 
      background:linear-gradient(135deg, #e3fde7 0%, #d3f8e1ff 100%); 
      border-radius:8px; 
      margin:15px 0; 
      border: 1px solid #c6e5f1
    }
    
    .comments-section {
      margin-top: 15px; 
      padding: 12px; 
      background: #f9f9f9; 
      border-radius: 8px; 
      border-left: 4px solid #02acf2
    }
    
    .comments-title {
      font-weight: bold; 
      margin-bottom: 8px; 
      color: #333
    }
    
    .comments-content {
      font-size: 11px; 
      line-height: 1.5; 
      color: #555
    }
    
    .total-amount-section {
      margin-top: 15px;
      padding: 15px;
      background: linear-gradient(135deg, #fff5f5 0%, #ffecec 100%);
      border-radius: 8px;
      border: 2px solid #ff6b6b;
    }
    
    .total-amount-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 15px;
    }
    
    .total-item {
      padding: 10px;
      background: white;
      border-radius: 6px;
      text-align: center;
    }
    
    .total-label {
      font-size: 12px;
      color: #666;
      margin-bottom: 5px;
    }
    
    .total-value {
      font-size: 18px;
      font-weight: bold;
    }
    
    .grand-total {
      font-size: 22px;
      font-weight: bold;
      color: #ff6b6b;
    }
    
    .save-section {
      margin: 20px 0;
      text-align: center;
    }
    
    .save-button {
      background: linear-gradient(135deg, #02acf2 0%, #0266f2 100%);
      color: white;
      border: none;
      padding: 10px 20px;
      border-radius: 5px;
      font-size: 14px;
      font-weight: bold;
      cursor: pointer;
      transition: all 0.3s ease;
    }
    
    .save-button:hover {
      background: linear-gradient(135deg, #0266f2 0%, #02acf2 100%);
      transform: translateY(-2px);
      box-shadow: 0 4px 8px rgba(2, 102, 242, 0.3);
    }
    
    .save-button:active {
      transform: translateY(0);
    }
    
    .invoice-status {
      padding: 4px 8px;
      border-radius: 12px;
      font-size: 11px;
      font-weight: bold;
    }
    
    .status-editing {
      background: #fff3cd;
      color: #856404;
    }
    
    .status-new {
      background: #d4edda;
      color: #155724;
    }
    
    .thank-you-type-indicator {
      display: inline-block;
      padding: 2px 8px;
      border-radius: 12px;
      font-size: 10px;
      font-weight: bold;
      margin-left: 8px;
      vertical-align: middle;
    }
    
    .type-homeCare {
      background: #d4edda;
      color: #155724;
      max-width:max-content;
    }
    
    .type-housekeeping {
      background: #cce5ff;
      color: #004085;
    }
    
    .type-security {
      background: #fff3cd;
      color: #856404;
    }
    
    .type-custom {
      background: #f8d7da;
      color: #721c24;
    }
    
    .type-default {
      background: #e2e3e5;
      color: #383d41;
    }
    
    @media only screen and (max-width: 767px) {
      .page { padding: 10px; }
      .header { 
        display: block; 
        padding: 10px; 
        margin: 0 -10px;
      }
      .header .h-left { 
        text-align: center; 
        margin-top: 10px; 
      }
      .title { font-size: 22px; }
      .photo-box img { 
        width: 100px; 
        height: 100px; 
        margin: 0 auto;
      }
      .payment-summary { grid-template-columns: 1fr; }
      .custom-invoice-grid { grid-template-columns: 1fr; }
      .meta div { margin: 4px 0; }
      .sec { margin-top: 10px; }
      .sec-body { padding: 10px; }
      .footer { 
        margin: 10px -10px -10px -10px; 
        padding: 6px 10px; 
        font-size: 9px;
        flex-direction: column;
        text-align: center;
        gap: 4px;
      }
      .footer div { margin: 2px 0; }
      .total-amount-grid { grid-template-columns: 1fr; }
      .thank-you-type-indicator {
        display: block;
        margin: 5px auto 0 auto  !important;
      }
    }
    
    @media only screen and (max-width: 480px) {
      .title { font-size: 18px; }
      .photo-box img { 
        width: 90px; 
        height: 90px; 
      }
      .custom-invoice-item {
        padding: 6px;
      }
      .thank-you {
        padding: 10px;
      }
      .thank-you h3 {
        font-size: 16px;
      }
      .total-item {
        padding: 8px;
      }
      .grand-total {
        font-size: 18px;
      }
    }
  </style>
</head>
<body>
<div class="page">
  <div style="margin: -16px -15px 8px -15px"><img src="${headerImage}" alt="Header" style="width:100%; object-fit: contain;" /></div>
  
  <div class="header">
    <div class="h-left">
      <h1 class="title">COMPANY SERVICE INVOICE</h1>
      <div class="subtitle">JenCeo Home Care Services & Traders</div>
      <div class="meta">
        <div><strong>Invoice Date:</strong> ${invoiceDate}</div>
        <div><strong>Invoice Number:</strong> ${generatedInvoiceNumber} 
        ${invoiceData.thankYouType && invoiceData.thankYouType !== 'default' ? `<span class="thank-you-type-indicator type-${invoiceData.thankYouType}" style="margin-left: 8px;">${invoiceData.thankYouType.toUpperCase()}</span>` : ''}
        </div>
        <div><strong>Company ID:</strong> ${companyId}</div>
      </div>
    </div>
    <div class="photo-box">
      <img src="${companyLogo}" alt="Company Logo" />
      <div class="rating" style="margin-top: 8px; padding:4px">
        <strong>${companyName}</strong>
      </div>
    </div>
  </div>
  
  ${buildCompanyDetailsTable()}
  

  <div class="sec">
    <div class="sec-title"><h3>Current Invoice Details <strong>${displayServiceDate} ${displayEndDate ? `to ${displayEndDate}` : ''}</strong></h3></div>
    <div class="sec-body">
      <div class="custom-invoice-section">
        <div class="custom-invoice-grid">
          <div class="custom-invoice-item">
            <div class="custom-invoice-label">Service Start Date</div>
            <div class="custom-invoice-value">${displayServiceDate}</div>
          </div>
          <div class="custom-invoice-item">
            <div class="custom-invoice-label">End Date</div>
            <div class="custom-invoice-value">${displayEndDate || 'N/A'}</div>
          </div>
          <div class="custom-invoice-item">
            <div class="custom-invoice-label">Days Count</div>
            <div class="custom-invoice-value" style="color: #02acf2; font-weight: bold;">${daysCount} days</div>
            <small class="muted">(Including starting date)</small>
          </div>
          <div class="custom-invoice-item">
            <div class="custom-invoice-label">Day Amount (₹)</div>
            <div class="custom-invoice-value" style="color: #0266f2; font-weight: bold;">${formatINR(dayAmount)}</div>
          </div>
          <div class="custom-invoice-item">
            <div class="custom-invoice-label">Invoice Date</div>
            <div class="custom-invoice-value">${invoiceDate}</div>
          </div>
          <div class="custom-invoice-item">
            <div class="custom-invoice-label">Invoice Amount</div>
            <div class="custom-invoice-value" style="color: #0266f2; font-weight: bold;">${formatINR(invoiceAmount)}</div>
            <small class="muted">(Days: ${daysCount} × Amount: ${formatINR(dayAmount)})</small>
          </div>
          <div class="custom-invoice-item">
            <div class="custom-invoice-label">Gap if Any</div>
            <div class="custom-invoice-value">${gapInfo}</div>
          </div>
          <div class="custom-invoice-item">
            <div class="custom-invoice-label">Traveling Charges</div>
            <div class="custom-invoice-value" style="color: #0266f2;">${formatINR(travelingCharges)}</div>
          </div>
          <div class="custom-invoice-item">
            <div class="custom-invoice-label">Extra Charges</div>
            <div class="custom-invoice-value" style="color: #0266f2;">${formatINR(extraCharges)}</div>
          </div>
          <div class="custom-invoice-item">
            <div class="total-label">Grand Total</div>
            <div class="total-value grand-total">${formatINR(totalAmount)}</div>
          </div>
        </div>
        ${invoiceData.remarks ? `
        <div style="margin-top: 12px; padding: 8px; background: white; border-radius: 6px; border: 1px solid #e5e5e5;">
          <div style="font-weight: 600; font-size: 11px; color: #555; margin-bottom: 4px;">Remarks</div>
          <div style="font-size: 12px; color: #333;">${invoiceData.remarks}</div>
        </div>
        ` : ''}
      </div>
    </div>
  </div>

  ${invoiceData.additionalComments ? `
  <div class="sec">
    <div class="sec-title"><h3>Additional Comments</h3></div>
    <div class="sec-body">
      <div class="comments-section">
        <div class="comments-content">${invoiceData.additionalComments}</div>
      </div>
    </div>
  </div>
  ` : ''}

  <div class="thank-you">
    <h3 style="color:#02acf2; margin-bottom:8px; font-size: 18px;">${thankYouMessage.title}</h3>
    <div style="margin:0; font-size: 12px; line-height: 1.6; text-align: center; padding: 0 10px;">
      ${thankYouMessage.message}
    </div>
    <p style="margin:5px 0 0 0; font-size: 14px;"><strong>JenCeo Home Care & Management</strong></p>
    <p style="margin:0; font-size:10px; color:#02acf2; margin-top:10px">Quality Service | Trusted Care | Client Satisfaction</p>
  </div>

  <div class="footer">
    <div>Invoice Ref: ${generatedInvoiceNumber}</div>
    <div>Generated On: ${currentDate}</div>
    <div>Page 1 of 1</div>
  </div>
</div>
</body>
</html>
`;

    return html;
  };

  const handleDownloadInvoice = (invoice = null) => {
    const html = buildInvoiceHTML();
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = `CompanyInvoice_${generatedInvoiceNumber}_${company?.companyName || 'company'}.html`;
    document.body.appendChild(a);
    a.click();

    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 100);
  };

  const handleShareInvoice = async () => {
    try {
      const html = buildInvoiceHTML();
      const blob = new Blob([html], { type: 'text/html' });

      if (navigator.share && navigator.canShare) {
        const file = new File(
          [blob],
          `CompanyInvoice_${generatedInvoiceNumber}.html`,
          { type: 'text/html' }
        );

        if (navigator.canShare({ files: [file] })) {
          await navigator.share({
            title: `Company Invoice - ${generatedInvoiceNumber}`,
            files: [file]
          });
          return;
        }
      }

      handleDownloadInvoice();
    } catch (e) {
      handleDownloadInvoice();
    }
  };

  const handlePrintInvoice = () => {
    const html = buildInvoiceHTML();
    const printWindow = window.open('', '_blank');
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();

    printWindow.onload = () => {
      printWindow.print();
      setTimeout(() => printWindow.close(), 100);
    };
  };

  const handleShareToWhatsApp = () => {
    const startDate = invoiceData.serviceDate;
    const endDate = invoiceData.endDate;
    const daysCount = calculateDaysCount(startDate, endDate);
    const dayAmount = parseFloat(invoiceData.dayAmount) || 0;
    const invoiceAmount = calculateInvoiceAmount(daysCount, dayAmount);
    const travelingCharges = parseFloat(invoiceData.travelingCharges) || 0;
    const extraCharges = parseFloat(invoiceData.extraCharges) || 0;
    const totalAmount = calculateTotalAmount(invoiceData);
    const totalPaid = paymentDetails.totalPaid || 0;
    const dueAmount = Math.max(0, totalAmount - totalPaid);

    const message = `*Company Invoice Details*\n\n` +
      `*Company:* ${company?.companyName || 'N/A'}\n` +
      `*Invoice Number:* ${generatedInvoiceNumber}\n` +
      `*Service Date:* ${formatDate(startDate)}\n` +
      `*End Date:* ${formatDate(endDate) || 'N/A'}\n` +
      `*Days Count:* ${daysCount} days\n` +
      `*Day Amount:* ₹${formatAmount(dayAmount)}\n` +
      `*Invoice Amount:* ₹${formatAmount(invoiceAmount)}\n` +
      `*Traveling Charges:* ₹${formatAmount(travelingCharges)}\n` +
      `*Extra Charges:* ₹${formatAmount(extraCharges)}\n` +
      `*Total Amount:* ₹${formatAmount(totalAmount)}\n` +
      `*Total Paid:* ₹${formatAmount(totalPaid)}\n` +
      `*Due Amount:* ₹${formatAmount(dueAmount)}\n` +
      `*Last Payment:* ${paymentDetails.lastPayment ? `₹${formatAmount(paymentDetails.lastPayment.amount)} on ${formatDate(paymentDetails.lastPayment.date)}` : 'No payments'}\n` +
      `*Next Payment Due:* ${invoiceData.nextPaymentDate ? formatDate(invoiceData.nextPaymentDate) : 'N/A'}\n` +
      `*Generated Date:* ${formatDate(new Date())}\n\n` +
      `*Assigned Worker:* ${invoiceData.workerName || 'N/A'} (ID: ${invoiceData.workerId || 'N/A'})\n` +
      `Thank you for your partnership!`;

    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/?text=${encodedMessage}`;

    window.open(whatsappUrl, '_blank');
  };

  const handleDeleteInvoice = (invoice) => {
    setInvoiceToDelete(invoice);
    setDeleteRemarks('');
    setShowDeleteConfirm(true);
  };

  // FIXED: Delete invoice with proper remarks and user info
  const confirmDeleteInvoice = async () => {
    if (!deleteRemarks.trim()) {
      alert("Please enter remarks before deleting invoice");
      return;
    }

    if (invoiceToDelete) {
      const currentUser = getCurrentUser();
      const updatedInvoice = {
        ...invoiceToDelete,
        isDeleted: true,
        deletedAt: new Date().toISOString(),
        deletedBy: currentUser,
        deletedReason: deleteRemarks.trim() || "No reason provided"
      };

      try {
        await saveInvoiceToFirebase(updatedInvoice);
      } catch (error) {
        console.error("Error deleting invoice in Firebase:", error);
      }

      setInvoiceHistory(prev => prev.filter(inv => inv.id !== invoiceToDelete.id));
      setDeletedInvoices(prev => [updatedInvoice, ...prev]);

      setSaveMessage({
        type: 'success',
        text: `Invoice #${invoiceToDelete.invoiceNumber} moved to deleted invoices.`
      });
      setTimeout(() => setSaveMessage({ type: '', text: '' }), 5000);
    }

    setShowDeleteConfirm(false);
    setInvoiceToDelete(null);
    setDeleteRemarks('');
  };

  const handleRestoreInvoice = (invoice) => {
    setInvoiceToRestore(invoice);
    setShowRestoreConfirm(true);
  };

  const confirmRestoreInvoice = async () => {
    if (invoiceToRestore) {
      const currentUser = getCurrentUser();
      const restoredInvoice = {
        ...invoiceToRestore,
        isDeleted: false,
        deletedAt: null,
        deletedBy: null,
        deletedReason: null,
        updatedBy: currentUser,
        updatedAt: new Date().toISOString()
      };

      try {
        await saveInvoiceToFirebase(restoredInvoice);
      } catch (error) {
        console.error("Error restoring invoice in Firebase:", error);
      }

      setDeletedInvoices(prev => prev.filter(inv => inv.id !== invoiceToRestore.id));
      setInvoiceHistory(prev => [restoredInvoice, ...prev]);

      setSaveMessage({
        type: 'success',
        text: `Invoice #${invoiceToRestore.invoiceNumber} restored successfully.`
      });
      setTimeout(() => setSaveMessage({ type: '', text: '' }), 5000);
    }

    setShowRestoreConfirm(false);
    setInvoiceToRestore(null);
  };

  // FIXED: Handle edit invoice button click
  const handleEditInvoice = (invoice) => {
    setInvoiceMode(INVOICE_MODE.EDIT);
    setIsEditingExisting(true);
    setEditingInvoiceId(invoice.id);

    setInvoiceData({
      ...invoice.data,
      invoiceNumber: invoice.invoiceNumber // Preserve the invoice number
    });

    setActiveTab('preview');
    setShowInvoiceModal(true);

    setTimeout(() => {
      if (iframeRef.current) {
        iframeRef.current.srcdoc = buildInvoiceHTML();
      }
    }, 100);
  };

  // FIXED: Handle edit invoice details button
  const handleEditInvoiceDetails = () => {
    setInvoiceMode(INVOICE_MODE.EDIT);
    setIsEditingExisting(true);
    setShowCustomInvoiceForm(true);
  };

  // FIXED: Handle view invoice button
  const handleViewInvoice = (invoice) => {
    setInvoiceMode(INVOICE_MODE.VIEW);
    setIsEditingExisting(false);
    setEditingInvoiceId(null);

    setInvoiceData({
      ...invoice.data,
      invoiceNumber: invoice.invoiceNumber
    });

    setActiveTab('preview');
    setShowInvoiceModal(true);

    setTimeout(() => {
      if (iframeRef.current) {
        iframeRef.current.srcdoc = buildInvoiceHTML();
      }
    }, 100);
  };

  const handleRecordPayment = () => {
    setShowPaymentModal(true);
  };

  const handlePaymentSubmit = async () => {
    if (!paymentData.amount || !paymentData.date) {
      alert('Please fill amount and date');
      return;
    }

    // Here you would save the payment to Firebase
    // For now, just show a success message
    setSaveMessage({
      type: 'success',
      text: `Payment of ₹${formatAmount(paymentData.amount)} recorded successfully for ${formatDate(paymentData.date)}`
    });

    // Reset form
    setPaymentData({
      amount: '',
      date: new Date().toISOString().split('T')[0],
      remarks: ''
    });
    setShowPaymentModal(false);

    setTimeout(() => {
      setSaveMessage({ type: '', text: '' });
    }, 5000);
  };

  // Function to download history table as HTML
  const downloadHistoryHTML = () => {
    const totalDays = invoiceHistory.reduce((sum, inv) => {
      const rawStartDate = inv.data.serviceDate;
      const rawEndDate = inv.data.endDate;
      return sum + calculateDaysCount(rawStartDate, rawEndDate);
    }, 0);

    const totalAmount = invoiceHistory.reduce((sum, inv) => {
      return sum + calculateTotalAmount(inv.data);
    }, 0);

    const dayAmountSum = invoiceHistory.reduce((sum, inv) => {
      return sum + (parseFloat(inv.data.dayAmount) || 0);
    }, 0);

    let html = `
<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Invoice History - ${company?.companyName || 'Company'}</title>
  <style>
    *{box-sizing:border-box}
    html,body{margin:0;padding:0;font-family:Arial,Helvetica,sans-serif;color:#111}
    .page{ max-width:1000px; margin:auto;background:#fff;border:1px solid #e5e5e5;padding:15px}
    .header{text-align:center; border-bottom:2px solid #b7b4b4; padding:15px; margin:0 -15px; background: linear-gradient(135deg, #c7d1f5 0%, #e3e8ff 100%); border-radius: 5px;}
    .title{font-size:28px;font-weight:700;letter-spacing:.4px;margin:0;background:linear-gradient(135deg, #02acf2 0%, #0266f2 100%);-webkit-background-clip:text;-webkit-text-fill-color:transparent}
    .subtitle{font-size:12px;color:#444;margin-top:2px; font-weight:900}
    table{width:100%;border-collapse:collapse;margin:20px 0}
    th{background:#02acf2;color:white;padding:10px;text-align:left;font-size:12px}
    td{padding:8px;border-bottom:1px solid #ddd;font-size:11px}
    tr:hover{background:#f5f5f5}
    .total-row{background:#333 !important;color:white;font-weight:bold}
    .footer{margin:20px -15px -15px -15px;font-size:10px; color:#fff;display:flex;justify-content:space-between; background:linear-gradient(135deg, #02acf2 0%, #0266f2 100%); padding:8px 15px}
    .thank-you{text-align:center; padding:15px; background:linear-gradient(135deg, #e3fde7 0%, #d3f8e1ff 100%); border-radius:8px; margin:15px 0; border: 1px solid #c6e5f1}
    @media print{.page{border:none;margin:0;width:100%}}
    .header-img{width:100%;max-height:80px;object-fit:contain;margin-bottom:6px}
  </style>
</head>
<body>
<div class="page">
  <div style="margin: -16px -15px 8px -15px"><img src="${headerImage}" alt="Header" style="width:100%; object-fit: contain;" /></div>
  
  <div class="header">
    <h1 class="title">INVOICE HISTORY REPORT</h1>
    <div class="subtitle">JenCeo Home Care Services & Traders</div>
    <div style="margin-top:10px; font-size:12px;">
      <strong>Company:</strong> ${company?.companyName || 'N/A'} | 
      <strong>Company ID:</strong> ${company?.companyId || 'N/A'} |
      <strong>Date:</strong> ${formatDate(new Date())}
    </div>
  </div>
  
 <div class="invoice-container">
  <!-- Desktop Header -->
  <div class="invoice-header desktop-only">
    <div class="invoice-header-row">
      <div class="invoice-col">Worker</div>
      <div class="invoice-col">Invoice #</div>
      <div class="invoice-col">Invoice Date</div>
      <div class="invoice-col">Service Period</div>
      <div class="invoice-col">Days</div>
      <div class="invoice-col">Day Rate</div>
      <div class="invoice-col">Amount</div>
    </div>
  </div>

  ${invoiceHistory.map((invoice) => {
      const rawStartDate = invoice.data.serviceDate;
      const rawEndDate = invoice.data.endDate;
      const daysCount = calculateDaysCount(rawStartDate, rawEndDate);
      const invoiceTotal = calculateTotalAmount(invoice.data);
      const workerSnapshot = invoice.data.workerSnapshot || {};
      const workerPhoto = enhancedResolvePhoto(workerSnapshot.photo || invoice.data.workerPhoto);

      return `
    <div class="invoice-card">
      <!-- Mobile Header -->
      <div class="invoice-card-header mobile-only">
        <div class="mobile-header-top">
          <div class="worker-info-mobile-header">
            <img src="${workerPhoto}" alt="Worker photo" class="worker-photo-mobile" />
            <div class="worker-details">
              <span class="worker-name">${workerSnapshot.workerName || invoice.data.workerName || 'N/A'}</span>
              <small class="worker-id">ID: ${workerSnapshot.workerId || invoice.data.workerId || 'N/A'}</small>
            </div>
          </div>
        </div>
        <div class="mobile-header-bottom">
          <div class="invoice-info-row">
            <div class="invoice-number">
              <span class="invoice-number-label">Invoice:</span>
              <span class="invoice-number-value">${invoice.invoiceNumber}</span>
            </div>
            <div class="invoice-date">
              <span class="invoice-date-label">Date:</span>
              <span class="invoice-date-value">${formatDate(invoice.date)}</span>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Card Content -->
      <div class="invoice-card-content">
        <!-- Desktop layout -->
        <div class="invoice-row desktop-only">
          <div class="invoice-col worker-info-desktop">
            <div class="worker-details-desktop">
              <img src="${workerPhoto}" alt="Worker photo" class="worker-photo-desktop" />
              <div class="worker-text">
                <div class="worker-name">${workerSnapshot.workerName || invoice.data.workerName || 'N/A'}</div>
                <div class="worker-id">ID: ${workerSnapshot.workerId || invoice.data.workerId || 'N/A'}</div>
              </div>
            </div>
          </div>
          <div class="invoice-col invoice-number-col">
            <span class="invoice-number-value">${invoice.invoiceNumber}</span>
          </div>
          <div class="invoice-col invoice-date-col">${formatDate(invoice.date)}</div>
          <div class="invoice-col service-date-col">
            <div class="service-date">
              ${formatDate(invoice.data.serviceDate)}
              ${invoice.data.endDate ?
        `<div class="service-date-to">to ${formatDate(invoice.data.endDate)}</div>` :
        ''
      }
            </div>
          </div>
          <div class="invoice-col days-count-col">
            <span class="days-count-badge">${daysCount}</span>
          </div>
          <div class="invoice-col day-amount-col">
            <div class="day-amount">₹${formatAmount(invoice.data.dayAmount || 0)}</div>
            <div class="day-label">Per Day</div>
          </div>
          <div class="invoice-col total-amount-col">
            <div class="total-amount">₹${formatAmount(invoiceTotal)}</div>
            <div class="total-label">total</div>
          </div>
        </div>
        
        <!-- Mobile layout -->
        <div class="mobile-only">
          <div class="invoice-details-grid">
            <div class="detail-row">
              <div class="detail-item">
                <span class="detail-label">Service Period</span>
                <span class="detail-value service-period">
                  ${formatDate(invoice.data.serviceDate)}
                  ${invoice.data.endDate ? ` - ${formatDate(invoice.data.endDate)}` : ''}
                </span>
              </div>
            </div>
            <div class="amounts-row">
              <div class="amount-item">
                <div class="amount-label">Days</div>
                <div class="amount-value days-count">${daysCount}</div>
              </div>
              <div class="amount-item">
                <div class="amount-label">Day Rate</div>
                <div class="amount-value day-amount">₹${formatAmount(invoice.data.dayAmount || 0)}</div>
              </div>
              <div class="amount-item total-item">
                <div class="amount-label">Total Amount</div>
                <div class="amount-value total-amount">₹${formatAmount(invoiceTotal)}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>`;
    }).join('')}

  <!-- Grand Total (Desktop) -->
  <div class="invoice-total-desktop desktop-only">
    <div class="total-grid">
      <div class="total-left">
        <div class="total-label">GRAND TOTAL</div>
        <div class="total-sub">${invoiceHistory.length} invoice${invoiceHistory.length !== 1 ? 's' : ''}</div>
      </div>
      <div class="total-right">
        <div class="total-days-badge">
          <span class="total-days-value">${totalDays}</span>
          <span class="total-days-label">days</span>
        </div>
        <div class="total-amount-main">
          ₹${formatAmount(totalAmount)}
        </div>
      </div>
    </div>
  </div>

  <!-- Grand Total (Mobile) -->
  <div class="invoice-total-mobile mobile-only">
    <div class="mobile-total-grid">
      <div class="mobile-total-header">
        <div class="mobile-total-label">GRAND TOTAL</div>
        <div class="mobile-total-count">${invoiceHistory.length} invoice${invoiceHistory.length !== 1 ? 's' : ''}</div>
      </div>
      <div class="mobile-total-body">
        <div class="mobile-total-days">
          <span class="total-days-value">${totalDays}</span>
          <span class="total-days-label">days</span>
        </div>
        <div class="mobile-total-amount">₹${formatAmount(totalAmount)}</div>
      </div>
    </div>
  </div>
</div>

<style>
.invoice-container {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  margin: 0 auto;
  width: 100%;
  max-width: 1400px;
  padding: 20px;
}

/* Desktop Styles */
.desktop-only {
  display: block;
}

/* Desktop Header */
.invoice-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px 12px 0 0;
  padding: 18px 24px;
  font-weight: 600;
  color: white;
  margin-bottom: 1px;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.1);
}

.invoice-header-row {
  display: grid;
  grid-template-columns: 1.5fr 0.8fr 0.9fr 1.2fr 0.6fr 0.8fr 1fr;
  gap: 16px;
  align-items: center;
  padding: 0 8px;
  font-size: 14px;
  letter-spacing: 0.3px;
}

/* Desktop Invoice Card */
.invoice-card {
  background: white;
  border-radius: 12px;
  margin-bottom: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid #f0f0f0;
  overflow: hidden;
}

.invoice-card:hover {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
  border-color: #e0e0e0;
}

.invoice-row {
  display: grid;
  grid-template-columns: 1.5fr 1.2fr 0.6fr .8fr 0.6fr 0.7fr 1fr;
  gap: 10px;
  align-items: center;
  padding: 15px;
  font-size: 14px;
  color: #333;
  text-align:center
}

.invoice-col {
  display: flex;
  align-items: center;
  min-height: 44px;
}

/* Worker Info Desktop */
.worker-info-desktop {
  display: flex;
  align-items: center;
}

.worker-details-desktop {
  display: flex;
  align-items: center;
  gap: 12px;
}

.worker-photo-desktop {
  width: 48px;
  height: 48px;
  border-radius: 10px;
  object-fit: cover;
  border: 2px solid #f5f5f5;
}

.worker-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.worker-name {
  font-weight: 600;
  color: #1a1a1a;
  font-size: 15px;
}

.worker-id {
  color: #666;
  font-size: 13px;
  font-weight: 400;
}

/* Invoice Number & Date Desktop */
.invoice-number-col,
.invoice-date-col {
  font-weight: 500;
  color: #444;
}

.invoice-number-value {
  font-family: 'SF Mono', Monaco, 'Cascadia Mono', monospace;
  font-size: 14px;
  color: #667eea;
  font-weight: 600;
}

/* Service Date Desktop */
.service-date-col {
  color: #555;
  line-height: 1.4;
}

.service-date-to {
  font-size: 12px;
  color: #888;
  margin-top: 2px;
}

/* Days Count Desktop */
.days-count-col {
  justify-content: center;
}

.days-count-badge {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 6px 12px;
  border-radius: 20px;
  font-weight: 700;
  font-size: 15px;
  min-width: 60px;
  text-align: center;
  box-shadow: 0 2px 4px rgba(102, 126, 234, 0.2);
}

/* Day Amount Desktop */
.day-amount-col {
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
}

.day-amount {
  font-weight: 700;
  color: #1a1a1a;
  font-size: 16px;
}

.day-label {
  color: #888;
  font-size: 12px;
  font-weight: 400;
}

/* Total Amount Desktop */
.total-amount-col {
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
}

.total-amount {
  font-weight: 800;
  color: #10b981;
  font-size: 18px;
  letter-spacing: 0.3px;
}

.total-label {
  color: #888;
  font-size: 12px;
  font-weight: 400;
}

/* Mobile Styles */
.mobile-only {
  display: none;
}

/* Mobile Header */
.invoice-card-header {
  background: linear-gradient(135deg, #6260dc 0%, #913e9b 100%);
  padding: 0;
  color: white;
}

.mobile-header-top {
  padding: 16px 16px 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.worker-info-mobile-header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.worker-photo-mobile {
  width: 48px;
  height: 48px;
  border-radius: 10px;
  object-fit: cover;
  border: 2px solid rgba(255, 255, 255, 0.2);
}

.worker-details {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.worker-name {
  font-weight: 600;
  font-size: 16px;
  color: #cd9afb
}

.worker-id {
  font-size: 13px;
  color:#cd9afb
}

.mobile-header-bottom {
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.05);
}

.invoice-info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.invoice-number,
.invoice-date {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.invoice-number-label,
.invoice-date-label {
  color: rgba(255, 255, 255, 0.6);
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.invoice-number-value,
.invoice-date-value {
  font-weight: 600;
  font-size: 15px;
  color: white;
}

.invoice-number-value {
  color: #66ddea;
}

/* Mobile Content */
.invoice-details-grid {
  padding: 20px;
}

.detail-row {
  margin-bottom: 20px;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
}

.detail-label {
  color: #666;
  font-size: 14px;
  font-weight: 500;
}

.detail-value {
  font-weight: 500;
  text-align: right;
  max-width: 60%;
}

.service-period {
  color: #e760ed;
  font-size: 15px;
  line-height: 1.4;
}

.amounts-row {
  display: flex;
  gap: 12px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 12px;
  padding: 16px;
  margin-top: 8px;
}

.amount-item {
  flex: 1;
  text-align: center;
  padding: 0 8px;
}

.amount-item.total-item {
  flex: 1.2;
}

.amount-label {
  color: #666;
  font-size: 12px;
  font-weight: 500;
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.amount-value {
  font-weight: 700;
  font-size: 18px;
}

.days-count {
  color: #667eea;
}

.day-amount {
  color: #764ba2;
}

.total-amount {
  color: #cf07d4;
  font-size: 20px;
}

/* Desktop Total */
.invoice-total-desktop {
  margin-top: 24px;
  background: linear-gradient(135deg, #880da2 0%, #7f0b71 100%);
  border-radius: 16px;
  padding: 28px 32px;
  color: white;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
}

.total-grid {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.total-left {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.total-label {
  font-size: 24px;
  font-weight: 700;
  color: white;
}

.total-sub {
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
}

.total-right {
  display: flex;
  align-items: center;
  gap: 32px;
}

.total-days-badge {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  background: rgba(255, 255, 255, 0.1);
  padding: 12px 20px;
  border-radius: 12px;
  min-width: 100px;
}

.total-days-value {
  font-size: 28px;
  font-weight: 800;
  color: #e266ea;
}

.total-days-label {
  color: rgba(255, 255, 255, 0.7);
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.total-amount-main {
  font-size: 36px;
  font-weight: 800;
  color: #faa6f6;
  letter-spacing: 0.5px;
}

/* Mobile Total */
.invoice-total-mobile {
  margin-top: 20px;
  background: linear-gradient(135deg, #880da2 0%, #7f0b71 100%);
  border-radius: 10px;
  padding: 20px;
  color: white;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}

.mobile-total-grid {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.mobile-total-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.mobile-total-label {
  font-size: 18px;
  font-weight: 700;
}

.mobile-total-count {
  color: rgba(255, 255, 255, 0.7);
  font-size: 13px;
}

.mobile-total-body {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.mobile-total-days {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.mobile-total-amount {
  font-size: 28px;
  font-weight: 800;
  color: #f5ba4d;
}

/* Responsive */
@media (max-width: 1024px) {
  .invoice-header-row,
  .invoice-row {
    grid-template-columns: 1.2fr 0.8fr 0.9fr 1fr 0.6fr 0.8fr 1fr;
    gap: 12px;
    padding: 16px 20px;
  }
}

@media (max-width: 768px) {
  .desktop-only {
    display: none !important;
  }
  
  .mobile-only {
    display: block !important;
  }
  
  .invoice-container {
    padding: 12px;
  }
  
  .invoice-card {
    margin-bottom: 16px;
  }
  
  .amounts-row {
    flex-direction: column;
    gap: 16px;
    background:#f1ebf25c
  }
  
  .amount-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0;
    text-align: left;
  }
  
  .amount-item.total-item {
    padding-top: 16px;
    border-top: 1px solid #e2e8f0;
  }
  
  .mobile-total-amount {
    font-size: 24px;
  }
}
</style>
  
  <div class="thank-you">
    <h3 style="color:#02acf2; margin-bottom:8px; font-size: 18px;">Thank You for Your Partnership!</h3>
    <div style="margin:0; font-size: 12px; line-height: 1.6; text-align: center; padding: 0 10px;">
      Dear <strong>${company?.companyName || 'Company'}</strong>,
      <br>
      We truly appreciate your trust and partnership with JenCeo Home Care Services.
      <br>
      We're here to support you whenever needed and look forward to our continued collaboration.
    </div>
    <p style="margin:5px 0 0 0; font-size: 14px;"><strong>JenCeo Home Care & Management</strong></p>
    <p style="margin:0; font-size:10px; color:#02acf2; margin-top:10px">Quality Service | Trusted Care | Client Satisfaction</p>
  </div>

  <div class="footer">
    <div>Company: ${company?.companyName || 'N/A'}</div>
    <div>Generated On: ${formatDate(new Date())}</div>
    <div>Total Invoices: ${invoiceHistory.length}</div>
  </div>
</div>
</body>
</html>`;

    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `InvoiceHistory_${company?.companyName || 'company'}_${formatDate(new Date())}.html`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 100);
  };

  const handleClear = () => {
    setShowPaymentModal(true);
  };

  if (!showInvoiceModal) {
    return (
      <div className="text-center p-4">
        <h5 className="mb-3">Company Invoice Generation</h5>
        <p className="small-text text-warning mb-4">Generate and share invoice for this company</p>
        <button
          type="button"
          className="btn btn-primary btn-lg"
          onClick={handleOpenInvoice}
        >
          <i className="bi bi-receipt me-2"></i>
          Generate Company Invoice
        </button>
      </div>
    );
  }

  return (
    <>
      {showDeleteConfirm && (
        <DeleteConfirmModal
          invoiceToDelete={invoiceToDelete}
          deleteRemarks={deleteRemarks}
          setDeleteRemarks={setDeleteRemarks}
          setShowDeleteConfirm={setShowDeleteConfirm}
          confirmDeleteInvoice={confirmDeleteInvoice}
          formatDate={formatDate}
          formatAmount={formatAmount}
        />
      )}

      {showRestoreConfirm && (
        <RestoreConfirmModal
          invoiceToRestore={invoiceToRestore}
          setShowRestoreConfirm={setShowRestoreConfirm}
          confirmRestoreInvoice={confirmRestoreInvoice}
          formatDate={formatDate}
          formatAmount={formatAmount}
        />
      )}

      {showPaymentModal && (
        <PaymentModal
          paymentData={paymentData}
          setPaymentData={setPaymentData}
          setShowPaymentModal={setShowPaymentModal}
          handlePaymentSubmit={handlePaymentSubmit}
          generatedInvoiceNumber={generatedInvoiceNumber}
          company={company}
          formatAmount={formatAmount}
          calculateTotalAmount={calculateTotalAmount}
          invoiceData={invoiceData}
          paymentDetails={paymentDetails}
        />
      )}

      {showCustomInvoiceForm && (
        <CustomInvoiceForm
          invoiceData={invoiceData}
          onApply={handleApplyCustomInvoice}
          onClose={() => setShowCustomInvoiceForm(false)}
          company={company}
          workers={workers}
          invoiceMode={invoiceMode}
          searchWorkerGlobally={searchWorkerGlobally}
          normalizeWorker={normalizeWorker}
          resolvePhoto={enhancedResolvePhoto}
          calculateDaysCount={calculateDaysCount}
          calculateInvoiceAmount={calculateInvoiceAmount}
          formatAmount={formatAmount}
        />
      )}

      {showThankYouMessageForm && (
        <ThankYouMessageForm
          onClose={(type, message) => {
            handleThankYouTypeChange(type, message);
            setShowThankYouMessageForm(false);
          }}
          company={company}
          invoiceData={invoiceData}
          thankYouMessages={thankYouMessages}
          determineServiceType={determineCompanyServiceType}
        />
      )}

      <div className="modal-card">
        <div className="mb-3">
          <h4 className="text-info">
            Company Invoice - {generatedInvoiceNumber}
            {invoiceMode === INVOICE_MODE.EDIT && (
              <span className="badge bg-warning ms-2">
                <i className="bi bi-pencil me-1"></i>
                Editing Invoice
              </span>
            )}
            {invoiceMode === INVOICE_MODE.VIEW && (
              <span className="badge bg-info ms-2">
                <i className="bi bi-eye me-1"></i>
                View Mode
              </span>
            )}
            {invoiceData.thankYouType && invoiceData.thankYouType !== 'default' && (
              <span className={`badge ${invoiceData.thankYouType === 'homeCare' ? 'bg-success' :
                invoiceData.thankYouType === 'housekeeping' ? 'bg-info' :
                  invoiceData.thankYouType === 'security' ? 'bg-warning text-dark' :
                    invoiceData.thankYouType === 'custom' ? 'bg-danger' :
                      'bg-light text-dark'
                } ms-2`}>
                {invoiceData.thankYouType && invoiceData.thankYouType.toUpperCase()} MESSAGE
              </span>
            )}
          </h4>
          <div className="d-flex flex-wrap gap-2 align-items-center">
            <button
              type="button"
              className={`btn btn-sm ${invoiceMode === INVOICE_MODE.EDIT ? 'btn-warning' : 'btn-primary'}`}
              onClick={handleEditInvoiceDetails}
            >
              <i className="bi bi-pencil-square me-1"></i>
              {invoiceMode === INVOICE_MODE.EDIT ? 'Edit Invoice Details' : 'Custom Invoice'}
            </button>

            <button
              type="button"
              className="btn btn-sm btn-info"
              onClick={() => {
                setShowThankYouMessageForm(true);
              }}
            >
              <i className="bi bi-chat-heart me-1"></i>
              Thank You Message
            </button>

            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              onClick={() => handleDownloadInvoice()}
            >
              <i className="bi bi-download me-1"></i>
              Download
            </button>
            <button
              type="button"
              className="btn btn-outline-success btn-sm"
              onClick={handleShareInvoice}
            >
              <i className="bi bi-share me-1"></i>
              Share
            </button>
            <button
              type="button"
              className="btn btn-outline-info btn-sm"
              onClick={handlePrintInvoice}
            >
              <i className="bi bi-printer me-1"></i>
              Print
            </button>
            <button
              type="button"
              className="btn btn-outline-warning btn-sm"
              onClick={handleShareToWhatsApp}
            >
              <i className="bi bi-whatsapp me-1"></i>
              WhatsApp
            </button>
            <button
              type="button"
              className="btn btn-outline-success btn-sm"
              onClick={handleRecordPayment}
            >
              <i className="bi bi-cash-stack me-1"></i>
              Record Payment
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={() => {
                setInvoiceMode(INVOICE_MODE.CREATE);
                setIsEditingExisting(false);
                setEditingInvoiceId(null);
                setShowInvoiceModal(false);
              }}
            >
              <i className="bi bi-x me-1"></i>
              Close
            </button>
          </div>
        </div>

        <div className="border-bottom">
          <ul className="nav nav-tabs">
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'preview' ? 'active' : ''}`}
                onClick={() => setActiveTab('preview')}
              >
                <i className="bi bi-eye me-1"></i>
                Preview
                {invoiceMode === INVOICE_MODE.EDIT && (
                  <span className="badge bg-warning ms-1">Editing</span>
                )}
                {invoiceMode === INVOICE_MODE.VIEW && (
                  <span className="badge bg-info ms-1">View</span>
                )}
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'history' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('history');
                  setInvoiceMode(INVOICE_MODE.CREATE);
                  setIsEditingExisting(false);
                  setEditingInvoiceId(null);
                }}
              >
                <i className="bi bi-clock-history me-1"></i>
                History ({invoiceHistory.length})
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'deleted' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('deleted');
                  setInvoiceMode(INVOICE_MODE.CREATE);
                  setIsEditingExisting(false);
                  setEditingInvoiceId(null);
                }}
              >
                <i className="bi bi-trash me-1"></i>
                Deleted ({deletedInvoices.length})
              </button>
            </li>
          </ul>
        </div>

        <div className="modal-card-body bill-wrapper">
          {activeTab === 'preview' ? (
            <>
              <iframe
                ref={iframeRef}
                title="Company Invoice Preview"
                style={{
                  width: "100%",
                  height: "650px",
                  border: "1px solid #e5e5e5",
                  borderRadius: 8,
                  background: "white"
                }}
              />

              {saveMessage.text && (
                <div className={`alert alert-${saveMessage.type === 'error' ? 'danger' : 'success'} alert-dismissible fade show mt-3`} role="alert" style={{ maxWidth: '600px', margin: '15px auto' }}>
                  {saveMessage.type === 'error' ? (
                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                  ) : (
                    <i className="bi bi-check-circle-fill me-2"></i>
                  )}
                  {saveMessage.text}
                  <button type="button" className="btn-close" onClick={() => setSaveMessage({ type: '', text: '' })}></button>
                </div>
              )}

              <div className="mt-3 text-center">
                {invoiceMode !== INVOICE_MODE.VIEW && (
                  <button
                    type="button"
                    className={`btn ${invoiceMode === INVOICE_MODE.EDIT ? 'btn-warning' : 'btn-primary'}`}
                    onClick={saveInvoiceToHistory}
                  >
                    <i className="bi bi-save me-1"></i>
                    {invoiceMode === INVOICE_MODE.EDIT ? 'Update Invoice' : 'Save Invoice to History'}
                  </button>
                )}
                {invoiceMode === INVOICE_MODE.EDIT && (
                  <button
                    type="button"
                    className="btn btn-outline-secondary ms-2"
                    onClick={() => {
                      setInvoiceMode(INVOICE_MODE.CREATE);
                      setIsEditingExisting(false);
                      setEditingInvoiceId(null);
                      setSelectedWorkerId('');
                      setInvoiceData({
                        serviceDate: '',
                        endDate: '',
                        invoiceDate: new Date().toISOString().split('T')[0],
                        dayAmount: company?.serviceCharges || '',
                        gapIfAny: '',
                        travelingCharges: '',
                        extraCharges: '',
                        remarks: '',
                        additionalComments: '',
                        serviceRemarks: '',
                        nextPaymentDate: '',
                        thankYouType: determineCompanyServiceType(),
                        customThankYou: '',
                        workerId: '',
                        workerName: '',
                        workerDepartment: '',
                        workerPhone: '',
                        workerPhoto: '',
                        invoiceNumber: '',
                        workerSnapshot: null
                      });

                      if (showThankYouMessageForm) {
                        setShowThankYouMessageForm(false);
                      }

                      setTimeout(() => {
                        if (iframeRef.current) {
                          iframeRef.current.srcdoc = buildInvoiceHTML();
                        }
                      }, 100);
                    }}
                  >
                    <i className="bi bi-x-circle me-1"></i>
                    Cancel Edit
                  </button>
                )}
              </div>
            </>
          ) : activeTab === 'history' ? (
            <>
              {saveMessage.text && (
                <div className={`alert alert-${saveMessage.type === 'error' ? 'danger' : 'success'} alert-dismissible fade show mb-3`} role="alert">
                  {saveMessage.type === 'error' ? (
                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                  ) : (
                    <i className="bi bi-check-circle-fill me-2"></i>
                  )}
                  {saveMessage.text}
                  <button type="button" className="btn-close" onClick={() => setSaveMessage({ type: '', text: '' })}></button>
                </div>
              )}
              <InvoiceHistoryTable
                invoiceHistory={invoiceHistory}
                deletedInvoices={deletedInvoices}
                handleViewInvoice={handleViewInvoice}
                handleEditInvoice={handleEditInvoice}
                handleDeleteInvoice={handleDeleteInvoice}
                handleRestoreInvoice={handleRestoreInvoice}
                handleDownloadInvoice={(invoice) => {
                  setInvoiceData(invoice.data);
                  handleDownloadInvoice();
                }}
                formatDate={formatDate}
                formatDateTime={formatDateTime}
                formatAmount={formatAmount}
                calculateDaysCount={calculateDaysCount}
                calculateTotalAmount={calculateTotalAmount}
                calculateInvoiceAmount={calculateInvoiceAmount}
                resolvePhoto={enhancedResolvePhoto}
                downloadHistoryHTML={downloadHistoryHTML}
              />
            </>
          ) : (
            <>
              {saveMessage.text && (
                <div className={`alert alert-${saveMessage.type === 'error' ? 'danger' : 'success'} alert-dismissible fade show mb-3`} role="alert">
                  {saveMessage.type === 'error' ? (
                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                  ) : (
                    <i className="bi bi-check-circle-fill me-2"></i>
                  )}
                  {saveMessage.text}
                  <button type="button" className="btn-close" onClick={() => setSaveMessage({ type: '', text: '' })}></button>
                </div>
              )}
              <DeletedInvoicesTable
                deletedInvoices={deletedInvoices}
                handleRestoreInvoice={handleRestoreInvoice}
                handleViewInvoice={handleViewInvoice}
                handleDownloadInvoice={(invoice) => {
                  setInvoiceData(invoice.data);
                  handleDownloadInvoice();
                }}
                formatDate={formatDate}
                formatDateTime={formatDateTime}
                formatAmount={formatAmount}
                calculateTotalAmount={calculateTotalAmount}
              />
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default CompanyInvoice;